//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import { SignIn } from 'aws-amplify-react';
import React from 'react';
import { connect } from 'react-redux';
import { Button } from 'unison';

import { getUserAuthTypeByEmail } from '@apis/auth/gatewayAuthenticationApi';
import AuthType from '@apis/auth/models/AuthType';
import { ICuralateStore } from '@models/AppState';
import { amplifyConfig } from 'app/initialization/amplify/config';
import logger from 'app/logger/logger';
import { ICognitoStateData } from 'app/initialization/routing/routingModels';
import { getPostLoginRedirect } from 'app/initialization/routing/selectors/getPostLoginRedirect';
import { CustomLabel } from './CustomLabel';
import { WideInput } from './WideInput';
import { runOauthForProvider } from '@apis/auth/oauthForProviderApi';

interface IOwnState {
  loading: boolean;
  authType?: AuthType;
}

const mapStateToProps = (state: ICuralateStore) => ({
  postLoginRedirect: getPostLoginRedirect(state),
});

// TODO: Refactor this whole component to not useaws-amplify classes.
class CuralateSignIn extends SignIn {
  public state: IOwnState;

  constructor(props: React.ComponentProps<typeof SignIn>) {
    super(props);
    this._validAuthStates = ['signIn', 'signedOut', 'signedUp'];

    this.onSubmit = this.onSubmit.bind(this);
  }

  // Use this instead of `setState` for proper typing for this derived class
  public setStateHack(s: Partial<Readonly<IOwnState>>) {
    return super.setState(s as { loading: boolean });
  }

  public showComponent() {
    const authType = this.state.authType;

    const inPasswordPhase = !!authType;

    return (
      <div>
        <h6>Sign in to your account</h6>
        <form onSubmit={this.onSubmit}>
          <CustomLabel label='Email address'>
            <WideInput
              autoFocus={true}
              placeholder='Enter your email address'
              key='username'
              name='username'
              type='email'
              onChange={this.handleInputChange}
            />
          </CustomLabel>
          {inPasswordPhase && this.renderPassword()}
          {inPasswordPhase && (
            <Button
              className='cu-button-primary cu-d-block cu-w-100'
              isDisabled={this.state.loading}
              type='submit'
            >
              Sign In
            </Button>
          )}
          {!inPasswordPhase && (
            <Button
              className='cu-button-primary cu-d-block cu-w-100'
              isDisabled={this.state.loading}
              type='submit'
            >
              Next
            </Button>
          )}
        </form>
      </div>
    );
  }

  protected onSubmit(event: React.FormEvent<HTMLFormElement>) {
    const inPasswordPhase = !!this.state.authType;
    if (inPasswordPhase) {
      return this.signIn(event);
    } else {
      return this.determineAuthType(event);
    }
  }

  protected signIn(event: React.FormEvent<HTMLFormElement>) {
    if (!(this.inputs.username && this.inputs.password)) {
      event.preventDefault();
      return;
    }
    this.inputs.username =
      this.inputs.username && this.inputs.username.toLowerCase();
    return super.signIn(event);
  }

  protected async determineAuthType(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!this.inputs.username) {
      return;
    }
    this.inputs.username =
      this.inputs.username && this.inputs.username.toLowerCase();
    this.setStateHack({ loading: true });
    const email = this.inputs.username;

    /*
    When an invalid user tries to login, system should not throw error that the user
    doesn't exist. We will be allowing the user to submit the password and let the generic error
    thrown rather than the user doesn't exist
    */
    const authType = await getUserAuthTypeByEmail(email).then((response) => {
      if (response === null) {
        return AuthType.UserPassword;
      } else return response;
    });

    logger.cloud.log(`Determined auth type for user ${email} as ${authType}`);

    if (authType === AuthType.UserPassword) {
      // Just continue with the password prompt
      this.setStateHack({ loading: false, authType });
    } else {
      // Should be a SAML provider identity name, run their oauth flow.
      // We are restricting the use of login_hint only for the Portal user but the same can be used by other OAuth Providers.
      // https://devforum.okta.com/t/how-to-pass-login-hint-into-authorization-url/26905
      runOauthForProvider(
        authType === AuthType.GoogleSSO ? AuthType('Google') : authType,
        this.buildCognitoStateParam(),
        authType === 'bv-portal-okta' || authType === 'bv-portal-sso' // TBD: using string constant
          ? email
          : undefined,
      );
    }
  }

  private buildCognitoStateParam(): ICognitoStateData {
    return {
      loginRedirect: this.props.postLoginRedirect,
    };
  }

  private renderPassword() {
    return (
      <CustomLabel label='Password'>
        <WideInput
          placeholder='Enter your password'
          key='password'
          name='password'
          type='password'
          onChange={this.handleInputChange}
        />
        <br />
        <Button
          className='cu-button-invisible cu-text-link-primary cu-my-2 cu-d-block'
          onClick={() => this.changeState('forgotPassword')}
        >
          Forgot password?
        </Button>
      </CustomLabel>
    );
  }
}

export default connect(mapStateToProps)(CuralateSignIn);
